import { useController } from '@framework/form/hooks/useController.ts';
import produce, { setAutoFreeze } from 'immer';
import { useEffect, useMemo, useState } from 'react';
import { WidgetsRootContextProvider } from '../../../../../../tree/context/WidgetRootContext.tsx';
import { TreeNavigatorLogic } from '../../../../../../tree/navigation/TreeNavigatorLogic.ts';
import {
  TreeReferencesContextProvider,
  useTreeReferencesContext,
} from '../../../../../../tree/references/TreeReferencesContext.tsx';
import { useEngineReactComponentsContext } from '../../../../hooks/useEngineReactComponentsContext.ts';
import { WidgetMainType, WidgetType } from '../../../../Widget.ts';
import { type IRootWidget } from '../../../containers/root/IRootWidget.ts';
import { type ISubsectionWidget } from '../ISubsectionWidget.ts';
import { SubsectionRendering } from '../SubsectionRendering.ts';

// TODO Spostare in entrypoint dei progetti (storybook, validator, web)
setAutoFreeze(false);

export interface ISubsectionEditorProps {
  renderingKind: SubsectionRendering;
}

export function SubsectionEditor(props: ISubsectionEditorProps) {
  const { renderingKind } = props;
  const {
    field: { value, onChange, onBlur },
  } = useController('children');

  const { root: globalRoot, widget } = useTreeReferencesContext();

  const [root, setRoot] = useState<IRootWidget>({
    type: WidgetType.Root,
    mainType: WidgetMainType.Root,
    order: null,
    path: null,
    children: value ?? [],
  } as IRootWidget);

  // Aggiorno il form ogni volta che la root cambia
  // biome-ignore lint/correctness/useExhaustiveDependencies: <tbd>
  useEffect(() => {
    onChange(root.children);
    onBlur();
  }, [root]);

  const { sortableWidgetEditor: SortableWidgetEditor } =
    useEngineReactComponentsContext();

  // Abbiamo bisogno di utilizzare una root differente in qusto punto, per
  // poter inserire fra i riferimenti anche le variabili locali (della sottosezione).
  const widgetId = widget?.id;
  const updatedGlobalRoot = useMemo(() => {
    return produce(globalRoot, (draft: IRootWidget | undefined) => {
      if (!draft || !widgetId) return;
      const subsection = TreeNavigatorLogic.byId(
        draft,
        widgetId,
      ) as ISubsectionWidget;
      if (!subsection) return;
      subsection.children = root.children;
    });
  }, [globalRoot, root, widgetId]);

  return (
    <WidgetsRootContextProvider root={updatedGlobalRoot ?? null}>
      <TreeReferencesContextProvider root={updatedGlobalRoot} widget={widget}>
        <SortableWidgetEditor
          root={root}
          setRoot={setRoot}
          availableWidgetFilter={widgetType => {
            // Disabilitiamo la possibilità di inserire una sottosezione in una sottosezione
            // e un divisore di pagina in una sottosezione se la visualizzazione è inline
            if (
              renderingKind === SubsectionRendering.Inline &&
              widgetType.type === WidgetType.Page
            ) {
              return false;
            }

            // Disabilitiamo la possibilità di inserire il widget immagine in una sottosezione
            if ((widgetType.type as string) === 'image') return false;

            return widgetType.type !== WidgetType.Subsection;
          }}
        />
      </TreeReferencesContextProvider>
    </WidgetsRootContextProvider>
  );
}
